import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { copyToClipboard } from '@/utils/clipboard';
import { toast } from '@/stores/toastStore';
import { useTranslation } from '@/i18n';

interface CodeBlockProps {
  language: string;
  value: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ language, value }) => {
  const [copied, setCopied] = useState(false);
  const { settings } = useAppStore();
  const { t } = useTranslation();

  const handleCopy = async () => {
    try {
      await copyToClipboard(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t.chat.copiedToClipboard);
    } catch (error) {
      toast.error(t.chat.copyFailed);
    }
  };

  return (
    <div className="relative group my-4">
      {/* An untagged fence is not "code" with a language to announce — a chart
          or a plain block. Keep the copy affordance, drop the empty label. */}
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2 rounded-t-md">
        <span className="text-sm text-gray-300 font-medium">{language || ''}</span>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 flex items-center space-x-1 px-2 py-1 text-xs text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded transition-all"
          title={t.chat.copyCode}
          aria-label={t.chat.copyCode}
        >
          {copied ? (
            <>
              <Check className="h-3 w-3" />
              <span>{t.chat.copied}</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              <span>{t.chat.copy}</span>
            </>
          )}
        </button>
      </div>
      {/* overflow-x-auto, never wrap: a bar chart that re-wraps at the window
          width stops being a chart. Narrow screens scroll it. */}
      <pre className={`bg-gray-900 text-gray-100 p-4 rounded-b-md overflow-x-auto whitespace-pre text-sm font-mono ${
        settings.fontSize === 'small' ? 'text-xs' : settings.fontSize === 'large' ? 'text-base' : 'text-sm'
      }`}>
        <code>{value}</code>
      </pre>
    </div>
  );
};